import type {
  HoroscopeScope,
  MutagenId,
  NormalizedHoroscope,
  NormalizedHoroscopeItem,
  PalaceId,
  StarId,
} from './domain.js';
import type { Feature } from './rules.js';
import type { RelationshipContextBundle } from './relationshipContext.js';
import { assertNormalizedHoroscopeContract } from './horoscopeContractValidation.js';
import { sha256Fingerprint } from './stable.js';

export type RelationshipTimingScope = Exclude<HoroscopeScope, 'natal'>;

export const RELATIONSHIP_TIMING_SCOPES: readonly RelationshipTimingScope[] = Object.freeze([
  'decadal',
  'minor',
  'yearly',
  'monthly',
  'daily',
  'hourly',
]);

export interface RelationshipTimingMutagenFact {
  mutagen: MutagenId;
  starId: StarId;
  starLabel: string;
  basis: NormalizedHoroscopeItem['mutagens'][number]['basis'];
}

export interface RelationshipTimingMovingStarFact {
  starId: StarId;
  starLabel: string;
}

export interface RelationshipTimingScopeFact {
  scope: RelationshipTimingScope;
  index: number;
  name: string;
  heavenlyStem: string;
  earthlyBranch: string;
  nominalAge?: number;
  focusNatalPalaceId: PalaceId;
  focusCyclePalaceId: PalaceId;
  mutagens: readonly RelationshipTimingMutagenFact[];
  focusMovingStars: readonly RelationshipTimingMovingStarFact[];
}

export interface RelationshipTimingPersonContext {
  role: 'A' | 'B';
  horoscopeFingerprint: string;
  scopes: readonly RelationshipTimingScopeFact[];
}

export interface RelationshipTimingSharedScopeFact {
  scope: RelationshipTimingScope;
  sameFocusCyclePalace: boolean;
  sharedMutagenKeys: readonly string[];
  sharedFocusMovingStarIds: readonly StarId[];
}

export interface RelationshipTimingContextBundle {
  model: 'RELATIONSHIP-TIMING-CONTEXT-0.1.0';
  relationshipType: RelationshipContextBundle['relationshipType'];
  relationshipContextFingerprint: string;
  targetSolarDate: string;
  targetLunarDate: string;
  targetTimeIndex: number;
  personA: RelationshipTimingPersonContext;
  personB: RelationshipTimingPersonContext;
  sharedScopes: readonly RelationshipTimingSharedScopeFact[];
  timingContextFingerprint: string;
  disclosure: string;
}

function itemForScope(horoscope: NormalizedHoroscope, scope: RelationshipTimingScope): NormalizedHoroscopeItem {
  return horoscope[scope];
}

function normalizeScopeFact(item: NormalizedHoroscopeItem, focusNatalPalaceId: PalaceId): RelationshipTimingScopeFact {
  const focusCyclePalaceId = item.cyclePalaceByNatalPalace[focusNatalPalaceId];
  if (!focusCyclePalaceId) throw new Error(`Relationship timing missing ${item.scope} focus palace mapping for ${focusNatalPalaceId}`);
  const movingStars = item.movingStarsByNatalPalace[focusNatalPalaceId] ?? [];
  return Object.freeze({
    scope: item.scope,
    index: item.index,
    name: item.name,
    heavenlyStem: item.heavenlyStem,
    earthlyBranch: item.earthlyBranch,
    ...(item.nominalAge !== undefined ? { nominalAge: item.nominalAge } : {}),
    focusNatalPalaceId,
    focusCyclePalaceId,
    mutagens: Object.freeze(item.mutagens
      .map((entry): RelationshipTimingMutagenFact => Object.freeze({ ...entry }))
      .sort((a, b) => `${a.mutagen}:${a.starId}:${a.basis}`.localeCompare(`${b.mutagen}:${b.starId}:${b.basis}`))),
    focusMovingStars: Object.freeze(movingStars
      .map((star): RelationshipTimingMovingStarFact => Object.freeze({ starId: star.id, starLabel: star.label }))
      .sort((a, b) => a.starId.localeCompare(b.starId))),
  });
}

function personTimingContext(
  role: 'A' | 'B',
  horoscope: NormalizedHoroscope,
  focusNatalPalaceId: PalaceId,
): RelationshipTimingPersonContext {
  return Object.freeze({
    role,
    horoscopeFingerprint: horoscope.horoscopeFingerprint,
    scopes: Object.freeze(RELATIONSHIP_TIMING_SCOPES.map((scope) => normalizeScopeFact(itemForScope(horoscope, scope), focusNatalPalaceId))),
  });
}

function mutagenKey(fact: RelationshipTimingMutagenFact): string {
  return `${fact.mutagen}:${fact.starId}:${fact.basis}`;
}

function sharedScopeFact(
  a: RelationshipTimingScopeFact,
  b: RelationshipTimingScopeFact,
): RelationshipTimingSharedScopeFact {
  const bMutagens = new Set(b.mutagens.map(mutagenKey));
  const bMovingStars = new Set(b.focusMovingStars.map((star) => star.starId));
  return Object.freeze({
    scope: a.scope,
    sameFocusCyclePalace: a.focusCyclePalaceId === b.focusCyclePalaceId,
    sharedMutagenKeys: Object.freeze(a.mutagens.map(mutagenKey).filter((key) => bMutagens.has(key)).sort()),
    sharedFocusMovingStarIds: Object.freeze(a.focusMovingStars.map((star) => star.starId).filter((id) => bMovingStars.has(id)).sort()),
  });
}

function assertTargetAlignment(a: NormalizedHoroscope, b: NormalizedHoroscope): void {
  const mismatches: string[] = [];
  if (a.targetSolarDate !== b.targetSolarDate) mismatches.push('targetSolarDate');
  if (a.targetLunarDate !== b.targetLunarDate) mismatches.push('targetLunarDate');
  if (a.targetTimeIndex !== b.targetTimeIndex) mismatches.push('targetTimeIndex');
  if (mismatches.length) throw new Error(`Relationship timing requires the same target date/time for both charts: ${mismatches.join(', ')}`);
}

export async function buildRelationshipTimingContext(
  relationship: RelationshipContextBundle,
  personAHoroscope: NormalizedHoroscope,
  personBHoroscope: NormalizedHoroscope,
): Promise<RelationshipTimingContextBundle> {
  assertNormalizedHoroscopeContract(personAHoroscope);
  assertNormalizedHoroscopeContract(personBHoroscope);
  if (personAHoroscope.natalChartFingerprint !== relationship.personA.chartFingerprint) {
    throw new Error('Relationship timing person A horoscope does not belong to relationship person A chart');
  }
  if (personBHoroscope.natalChartFingerprint !== relationship.personB.chartFingerprint) {
    throw new Error('Relationship timing person B horoscope does not belong to relationship person B chart');
  }
  assertTargetAlignment(personAHoroscope, personBHoroscope);

  const personA = personTimingContext('A', personAHoroscope, relationship.personA.focusPalaceId);
  const personB = personTimingContext('B', personBHoroscope, relationship.personB.focusPalaceId);
  const sharedScopes = Object.freeze(RELATIONSHIP_TIMING_SCOPES.map((scope) => {
    const a = personA.scopes.find((item) => item.scope === scope)!;
    const b = personB.scopes.find((item) => item.scope === scope)!;
    return sharedScopeFact(a, b);
  }));
  const fingerprintBasis = {
    model: 'RELATIONSHIP-TIMING-CONTEXT-0.1.0',
    relationshipContextFingerprint: relationship.contextFingerprint,
    personAHoroscopeFingerprint: personA.horoscopeFingerprint,
    personBHoroscopeFingerprint: personB.horoscopeFingerprint,
    targetSolarDate: personAHoroscope.targetSolarDate,
    targetLunarDate: personAHoroscope.targetLunarDate,
    targetTimeIndex: personAHoroscope.targetTimeIndex,
  } as const;
  return Object.freeze({
    model: 'RELATIONSHIP-TIMING-CONTEXT-0.1.0',
    relationshipType: relationship.relationshipType,
    relationshipContextFingerprint: relationship.contextFingerprint,
    targetSolarDate: personAHoroscope.targetSolarDate,
    targetLunarDate: personAHoroscope.targetLunarDate,
    targetTimeIndex: personAHoroscope.targetTimeIndex,
    personA,
    personB,
    sharedScopes,
    timingContextFingerprint: await sha256Fingerprint(fingerprintBasis),
    disclosure: '此層只把兩人的大限、小限、流年、流月、流日、流時在同一目標日期上對齊，整理焦點宮映射、四化與流耀 Evidence；不產生事件必然性、關係吉凶總分或相合率。',
  });
}

function timingEvidence(role: 'A' | 'B' | 'BOTH', scope: RelationshipTimingScope, parts: readonly string[]): string {
  return ['RELATIONSHIP-TIMING', role, scope, ...parts].join(':').toUpperCase();
}

export function extractRelationshipTimingFeatures(context: RelationshipTimingContextBundle): readonly Feature[] {
  const features: Feature[] = [];
  for (const person of [context.personA, context.personB] as const) {
    const role = person.role.toLowerCase();
    for (const item of person.scopes) {
      features.push({
        id: `relationship_timing.${role}.${item.scope}.focus_cycle_palace.${item.focusCyclePalaceId}`,
        scope: item.scope,
        kind: 'relationship_timing_focus_palace_mapping',
        value: true,
        evidenceRefs: [timingEvidence(person.role, item.scope, ['FOCUS-MAP', item.focusNatalPalaceId, item.focusCyclePalaceId])],
        evidenceGroup: `relationship-timing-${role}-${item.scope}-focus-map`,
      });
      features.push({
        id: `relationship_timing.${role}.${item.scope}.heavenly_stem.${item.heavenlyStem}`,
        scope: item.scope,
        kind: 'relationship_timing_heavenly_stem',
        value: true,
        evidenceRefs: [timingEvidence(person.role, item.scope, ['HEAVENLY-STEM', item.heavenlyStem])],
        evidenceGroup: `relationship-timing-${role}-${item.scope}-cycle-core`,
      });
      features.push({
        id: `relationship_timing.${role}.${item.scope}.earthly_branch.${item.earthlyBranch}`,
        scope: item.scope,
        kind: 'relationship_timing_earthly_branch',
        value: true,
        evidenceRefs: [timingEvidence(person.role, item.scope, ['EARTHLY-BRANCH', item.earthlyBranch])],
        evidenceGroup: `relationship-timing-${role}-${item.scope}-cycle-core`,
      });
      for (const transformation of item.mutagens) {
        features.push({
          id: `relationship_timing.${role}.${item.scope}.mutagen.${transformation.mutagen}.star.${transformation.starId}`,
          scope: item.scope,
          kind: 'relationship_timing_four_transformation',
          value: true,
          evidenceRefs: [timingEvidence(person.role, item.scope, ['MUTAGEN', transformation.mutagen, transformation.starId, transformation.basis])],
          evidenceGroup: `relationship-timing-${role}-${item.scope}-transformations`,
        });
      }
      for (const star of item.focusMovingStars) {
        features.push({
          id: `relationship_timing.${role}.${item.scope}.focus_moving_star.${star.starId}`,
          scope: item.scope,
          kind: 'relationship_timing_focus_moving_star',
          value: true,
          evidenceRefs: [timingEvidence(person.role, item.scope, ['FOCUS-MOVING-STAR', item.focusNatalPalaceId, star.starId])],
          evidenceGroup: `relationship-timing-${role}-${item.scope}-moving-stars`,
        });
      }
    }
  }

  for (const shared of context.sharedScopes) {
    if (shared.sameFocusCyclePalace) {
      features.push({
        id: `relationship_timing.both.${shared.scope}.same_focus_cycle_palace`,
        scope: shared.scope,
        kind: 'relationship_timing_shared_focus_mapping',
        value: true,
        evidenceRefs: [timingEvidence('BOTH', shared.scope, ['SAME-FOCUS-CYCLE-PALACE'])],
        evidenceGroup: `relationship-timing-both-${shared.scope}-shared-structure`,
      });
    }
    for (const key of shared.sharedMutagenKeys) {
      features.push({
        id: `relationship_timing.both.${shared.scope}.shared_mutagen.${key.replaceAll(':', '.')}`,
        scope: shared.scope,
        kind: 'relationship_timing_shared_transformation_fact',
        value: true,
        evidenceRefs: [timingEvidence('BOTH', shared.scope, ['SHARED-MUTAGEN', key])],
        evidenceGroup: `relationship-timing-both-${shared.scope}-shared-structure`,
      });
    }
    for (const starId of shared.sharedFocusMovingStarIds) {
      features.push({
        id: `relationship_timing.both.${shared.scope}.shared_focus_moving_star.${starId}`,
        scope: shared.scope,
        kind: 'relationship_timing_shared_moving_star_fact',
        value: true,
        evidenceRefs: [timingEvidence('BOTH', shared.scope, ['SHARED-FOCUS-MOVING-STAR', starId])],
        evidenceGroup: `relationship-timing-both-${shared.scope}-shared-structure`,
      });
    }
  }

  return Object.freeze(features.sort((a, b) => a.id.localeCompare(b.id)));
}

export interface RelationshipTimingLayerViewModel {
  scope: RelationshipTimingScope;
  label: string;
  personAFocusCyclePalaceId: PalaceId;
  personBFocusCyclePalaceId: PalaceId;
  sharedMutagenCount: number;
  sharedFocusMovingStarCount: number;
  sameFocusCyclePalace: boolean;
}

export interface RelationshipTimingViewModel {
  targetSolarDate: string;
  targetLunarDate: string;
  targetTimeIndex: number;
  layers: readonly RelationshipTimingLayerViewModel[];
  disclosure: string;
}

const TIMING_LABELS: Readonly<Record<RelationshipTimingScope, string>> = Object.freeze({
  decadal: '大限',
  minor: '小限',
  yearly: '流年',
  monthly: '流月',
  daily: '流日',
  hourly: '流時',
});

export function relationshipTimingViewModel(context: RelationshipTimingContextBundle): RelationshipTimingViewModel {
  return Object.freeze({
    targetSolarDate: context.targetSolarDate,
    targetLunarDate: context.targetLunarDate,
    targetTimeIndex: context.targetTimeIndex,
    layers: Object.freeze(context.sharedScopes.map((shared) => {
      const a = context.personA.scopes.find((item) => item.scope === shared.scope)!;
      const b = context.personB.scopes.find((item) => item.scope === shared.scope)!;
      return Object.freeze({
        scope: shared.scope,
        label: TIMING_LABELS[shared.scope],
        personAFocusCyclePalaceId: a.focusCyclePalaceId,
        personBFocusCyclePalaceId: b.focusCyclePalaceId,
        sharedMutagenCount: shared.sharedMutagenKeys.length,
        sharedFocusMovingStarCount: shared.sharedFocusMovingStarIds.length,
        sameFocusCyclePalace: shared.sameFocusCyclePalace,
      });
    })),
    disclosure: '同期間疊加只呈現雙方運限結構是否重疊，以及各自焦點宮、四化與流耀 Evidence；沒有 compatibility score、吉凶總分或事件發生機率。',
  });
}
